import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Card, CardContent } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../components/ui/sheet";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { MapContainer, TileLayer, CircleMarker } from "react-leaflet";
import { REGIONES_CHILE } from "../lib/constants";
import { Semaforo, StatusBadge , EstadoBadge } from "../components/Semaforo";
import { formatDate } from "../lib/format";
import {
  Users, Buildings, GraduationCap, ListChecks, Briefcase, Calendar,
  User, Phone, EnvelopeSimple, MapPin, ArrowSquareOut, Bank
} from "@phosphor-icons/react";
import L from "leaflet";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const COLORS = {
  community: "#0F766E",
  comunal: "#1D4ED8",
  gobierno: "#7C3AED",
  gore: "#DB2777",
  organizacion: "#D97706",
};

const ENTITY_LABELS = {
  community: "Comunidad",
  comunal: "Aut. Comunal",
  gobierno: "Gobierno",
  gore: "GORE",
  organizacion: "Organización",
};

export default function MapPage() {
  const [data, setData] = useState({ communities: [], authorities: [] });
  const [filterRegion, setFilterRegion] = useState("");
  const [filterEntity, setFilterEntity] = useState("");
  const [selected, setSelected] = useState(null); // { type, id }
  const [context, setContext] = useState(null);
  const [loadingCtx, setLoadingCtx] = useState(false);

  useEffect(() => { api.get("/dashboard/map").then((r) => setData(r.data)); }, []);

  useEffect(() => {
    if (!selected) { setContext(null); return; }
    setLoadingCtx(true);
    const url = selected.type === "community"
      ? `/map/community/${selected.id}/context`
      : `/map/authority/${selected.id}/context`;
    api.get(url).then((r) => setContext(r.data)).finally(() => setLoadingCtx(false));
  }, [selected]);

  const points = useMemo(() => {
    let coms = data.communities.map(c => ({ ...c, type: "community" }));
    let auths = data.authorities.map(a => ({ ...a, type: a.entity_type }));
    let all = [...coms, ...auths];
    if (filterRegion) all = all.filter(p => p.region === filterRegion);
    if (filterEntity) all = all.filter(p => p.type === filterEntity);
    return all;
  }, [data, filterRegion, filterEntity]);

  const center = points.length > 0 ? [points[0].lat, points[0].lng] : [-33.45, -70.65];

  return (
    <div data-testid="map-page">
      <PageHeader title="Mapa territorial" description="Geolocalización de comunidades, autoridades, GORE y organizaciones. Haz click en un marcador para ver su ficha completa, directorio de autoridades y entidades vinculadas." />
      <div className="p-6 space-y-4">
        <Card className="rounded-md">
          <CardContent className="p-4 flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Región</Label>
              <Select value={filterRegion || "__all__"} onValueChange={(v) => setFilterRegion(v === "__all__" ? "" : v)}>
                <SelectTrigger className="w-56" data-testid="map-region-select"><SelectValue placeholder="Todas" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todas</SelectItem>
                  {REGIONES_CHILE.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Tipo entidad</Label>
              <Select value={filterEntity || "__all__"} onValueChange={(v) => setFilterEntity(v === "__all__" ? "" : v)}>
                <SelectTrigger className="w-56" data-testid="map-entity-select"><SelectValue placeholder="Todas" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todas</SelectItem>
                  <SelectItem value="community">Comunidades</SelectItem>
                  <SelectItem value="comunal">Aut. comunales</SelectItem>
                  <SelectItem value="gobierno">Gobierno</SelectItem>
                  <SelectItem value="gore">GORE</SelectItem>
                  <SelectItem value="organizacion">Organizaciones</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3 ml-auto text-xs flex-wrap">
              {Object.entries(COLORS).map(([k, c]) => (
                <div key={k} className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: c }} /> <span>{ENTITY_LABELS[k]}</span></div>
              ))}
            </div>
          </CardContent>
        </Card>

        <div className="rounded-md border border-slate-200 overflow-hidden bg-white" style={{ height: "70vh" }} data-testid="leaflet-map">
          <MapContainer center={center} zoom={5} style={{ height: "100%", width: "100%" }}>
            <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            {points.map((p) => (
              <CircleMarker
                key={`${p.type}-${p.id}`}
                center={[p.lat, p.lng]}
                radius={10}
                pathOptions={{ color: COLORS[p.type] || "#0F172A", fillColor: COLORS[p.type] || "#0F172A", fillOpacity: 0.75, weight: 2 }}
                eventHandlers={{
                  click: () => setSelected({ type: p.type === "community" ? "community" : "authority", id: p.id }),
                }}
              />
            ))}
          </MapContainer>
        </div>
        <div className="text-xs text-slate-500">{points.length} entidades en mapa. Click en un marcador para ver detalle.</div>
      </div>

      {/* Side panel */}
      <Sheet open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto p-0" data-testid="map-detail-sheet">
          {loadingCtx && <div className="p-8 text-slate-500">Cargando ficha…</div>}
          {!loadingCtx && context && selected?.type === "community" && (
            <CommunityContext context={context} onClose={() => setSelected(null)} />
          )}
          {!loadingCtx && context && selected?.type === "authority" && (
            <AuthorityContext context={context} onClose={() => setSelected(null)} />
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function CommunityContext({ context }) {
  const c = context.community;
  return (
    <div>
      <div className="px-6 py-5 border-b border-slate-200 bg-slate-900 text-white">
        <div className="text-xs uppercase tracking-[0.18em] text-slate-300 flex items-center gap-1.5"><Users size={12} weight="fill" /> Comunidad</div>
        <h2 className="font-display text-2xl font-bold mt-1">{c.name}</h2>
        <div className="text-xs text-slate-300 flex items-center gap-1 mt-1"><MapPin size={12} /> {c.region || "—"} · {c.comuna || "—"} {c.localizacion ? `· ${c.localizacion}` : ""}</div>
        <div className="mt-3 flex items-center gap-2">
          <StatusBadge tone={c.tipo_vinculo === "Operacional" ? "info" : "neutral"}>{c.tipo_vinculo}</StatusBadge>
          <Link to={`/comunidades/${c.id}`}>
            <Button size="sm" variant="secondary" className="h-7"><ArrowSquareOut size={12} className="mr-1" /> Ver ficha completa</Button>
          </Link>
        </div>
      </div>

      <div className="p-6 space-y-5">
        {/* Datos censales */}
        <div className="grid grid-cols-3 gap-3">
          <Mini label="Habitantes" value={c.habitantes || 0} />
          <Mini label="Hombres" value={c.hombres || 0} />
          <Mini label="Mujeres" value={c.mujeres || 0} />
        </div>
        {c.description && <p className="text-sm text-slate-700 leading-relaxed">{c.description}</p>}

        <Tabs defaultValue="directory">
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger value="directory" data-testid="tab-directory">Directorio</TabsTrigger>
            <TabsTrigger value="orgs">Org. y Edu.</TabsTrigger>
            <TabsTrigger value="commits">Compromisos</TabsTrigger>
            <TabsTrigger value="projects">Proyectos</TabsTrigger>
          </TabsList>

          <TabsContent value="directory" className="mt-4 space-y-3">
            <div className="text-xs uppercase tracking-wider text-slate-500">Autoridades de {c.comuna || "la comuna"}</div>
            {context.directory.length === 0 ? (
              <div className="text-sm text-slate-500 py-4">Sin autoridades registradas en esta comuna.</div>
            ) : (
              context.directory.map((d) => (
                <div key={d.authority.id} className="rounded-md border border-slate-200 bg-white">
                  <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Bank size={14} className="text-slate-600" />
                      <div>
                        <div className="font-medium text-sm">{d.authority.name}</div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500">{ENTITY_LABELS[d.authority.entity_type] || d.authority.entity_type}</div>
                      </div>
                    </div>
                    <Link to={`/${detailRouteForEntity(d.authority.entity_type)}/${d.authority.id}`}>
                      <Button size="sm" variant="ghost" className="h-7"><ArrowSquareOut size={14} /></Button>
                    </Link>
                  </div>
                  {d.members.length === 0 ? (
                    <div className="px-4 py-3 text-xs text-slate-500">Sin integrantes registrados.</div>
                  ) : (
                    <ul className="divide-y divide-slate-100">
                      {d.members.map((m) => (
                        <li key={m.id} className="px-4 py-2.5 flex items-center gap-3">
                          <div className="h-9 w-9 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                            {m.foto_url ? <img src={m.foto_url} alt="" className="h-full w-full object-cover" /> : <User size={16} />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">{m.name}</div>
                            <div className="text-xs text-slate-500 capitalize">
                              {m.role.replace("_", " ")}{m.adhesion_politica ? ` · ${m.adhesion_politica}` : ""}{m.profesion ? ` · ${m.profesion}` : ""}
                            </div>
                          </div>
                          <div className="hidden sm:flex flex-col items-end gap-0.5 text-xs text-slate-600">
                            {m.email && <span className="flex items-center gap-1"><EnvelopeSimple size={11} />{m.email}</span>}
                            {m.telefono && <span className="flex items-center gap-1"><Phone size={11} />{m.telefono}</span>}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))
            )}
          </TabsContent>

          <TabsContent value="orgs" className="mt-4 space-y-4">
            <Section title="Establecimientos educacionales" icon={GraduationCap} count={context.educational_facilities.length}>
              {context.educational_facilities.length === 0 ? (
                <Empty />
              ) : (
                <ul className="divide-y divide-slate-100">
                  {context.educational_facilities.map((e) => (
                    <li key={e.id} className="py-2.5">
                      <div className="text-sm font-medium">{e.name}</div>
                      <div className="text-xs text-slate-500">{e.tipo} · matrícula: {e.matricula || 0} {e.directivo ? `· ${e.directivo}` : ""}</div>
                    </li>
                  ))}
                </ul>
              )}
            </Section>
            <Section title="Organizaciones comunitarias" icon={Buildings} count={context.community_organizations.length}>
              {context.community_organizations.length === 0 ? <Empty /> : (
                <ul className="divide-y divide-slate-100">
                  {context.community_organizations.map((o) => (
                    <li key={o.id} className="py-2.5">
                      <div className="text-sm font-medium">{o.name}</div>
                      <div className="text-xs text-slate-500">{o.tipo}{o.description ? ` · ${o.description}` : ""}</div>
                    </li>
                  ))}
                </ul>
              )}
            </Section>
            <Section title="Instituciones presentes" icon={Buildings} count={context.local_institutions.length}>
              {context.local_institutions.length === 0 ? <Empty /> : (
                <ul className="divide-y divide-slate-100">
                  {context.local_institutions.map((i) => (
                    <li key={i.id} className="py-2.5">
                      <div className="text-sm font-medium">{i.name}</div>
                      <div className="text-xs text-slate-500">{i.tipo}{i.contacto ? ` · ${i.contacto}` : ""}</div>
                    </li>
                  ))}
                </ul>
              )}
            </Section>
          </TabsContent>

          <TabsContent value="commits" className="mt-4">
            <Section title="Compromisos" icon={ListChecks} count={context.commitments.length}>
              {context.commitments.length === 0 ? <Empty /> : (
                <ul className="divide-y divide-slate-100">
                  {context.commitments.map((cm) => (
                    <li key={cm.id} className="py-2.5 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{cm.name}</div>
                        <div className="text-xs text-slate-500">{cm.tipo} · {formatDate(cm.fecha_compromiso) || "sin fecha"}</div>
                      </div>
                      <Semaforo value={cm.semaforo} />
                    </li>
                  ))}
                </ul>
              )}
            </Section>
          </TabsContent>

          <TabsContent value="projects" className="mt-4 space-y-4">
            <Section title="Proyectos" icon={Briefcase} count={context.projects.length}>
              {context.projects.length === 0 ? <Empty /> : (
                <ul className="divide-y divide-slate-100">
                  {context.projects.map((p) => (
                    <li key={p.id} className="py-2.5 flex items-center justify-between">
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{p.name}</div>
                        <div className="text-xs text-slate-500">{p.eje_estrategico || "—"} · {p.responsable || "Sin responsable"}</div>
                      </div>
                      <EstadoBadge value={p.estado} />
                    </li>
                  ))}
                </ul>
              )}
            </Section>
            <Section title="Reuniones recientes" icon={Calendar} count={context.meetings.length}>
              {context.meetings.length === 0 ? <Empty /> : (
                <ul className="divide-y divide-slate-100">
                  {context.meetings.map((m) => (
                    <li key={m.id} className="py-2.5 flex items-center justify-between">
                      <div className="min-w-0">
                        <Link to={`/reuniones/${m.id}`} className="text-sm font-medium hover:underline">{m.asunto}</Link>
                        <div className="text-xs text-slate-500">{formatDate(m.fecha)} {m.lugar ? `· ${m.lugar}` : ""}</div>
                      </div>
                      <Link to={`/reuniones/${m.id}`}>
                        <Button size="sm" variant="ghost" className="h-7"><ArrowSquareOut size={14} /></Button>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </Section>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function AuthorityContext({ context }) {
  const a = context.authority;
  return (
    <div>
      <div className="px-6 py-5 border-b border-slate-200 bg-slate-900 text-white">
        <div className="text-xs uppercase tracking-[0.18em] text-slate-300 flex items-center gap-1.5"><Bank size={12} weight="fill" /> {ENTITY_LABELS[a.entity_type] || "Autoridad"}</div>
        <h2 className="font-display text-2xl font-bold mt-1">{a.name}</h2>
        <div className="text-xs text-slate-300 flex items-center gap-1 mt-1"><MapPin size={12} /> {a.region || "—"} · {a.comuna || "—"}</div>
        <div className="mt-3 flex items-center gap-2">
          <StatusBadge tone="info">{a.tipo_vinculo}</StatusBadge>
          <Link to={`/${detailRouteForEntity(a.entity_type)}/${a.id}`}>
            <Button size="sm" variant="secondary" className="h-7"><ArrowSquareOut size={12} className="mr-1" /> Ver ficha completa</Button>
          </Link>
        </div>
      </div>
      <div className="p-6 space-y-5">
        {a.description && <p className="text-sm text-slate-700">{a.description}</p>}

        <Section title="Directorio / Integrantes" icon={User} count={context.members.length}>
          {context.members.length === 0 ? <Empty text="Sin integrantes registrados." /> : (
            <ul className="divide-y divide-slate-100">
              {context.members.map((m) => (
                <li key={m.id} className="py-2.5 flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {m.foto_url ? <img src={m.foto_url} alt="" className="h-full w-full object-cover" /> : <User size={18} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">{m.name}</div>
                    <div className="text-xs text-slate-500 capitalize">{m.role.replace("_"," ")}{m.adhesion_politica ? ` · ${m.adhesion_politica}` : ""}{m.profesion ? ` · ${m.profesion}` : ""}</div>
                    {(m.email || m.telefono) && (
                      <div className="text-xs text-slate-500 flex items-center gap-3 mt-0.5">
                        {m.email && <span className="flex items-center gap-1"><EnvelopeSimple size={11} />{m.email}</span>}
                        {m.telefono && <span className="flex items-center gap-1"><Phone size={11} />{m.telefono}</span>}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Section>

        {context.nearby_communities?.length > 0 && (
          <Section title="Comunidades en la misma comuna" icon={Users} count={context.nearby_communities.length}>
            <ul className="divide-y divide-slate-100">
              {context.nearby_communities.map((c) => (
                <li key={c.id} className="py-2.5 flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium">{c.name}</div>
                    <div className="text-xs text-slate-500">{c.tipo_vinculo} · {c.habitantes || 0} habitantes</div>
                  </div>
                  <Link to={`/comunidades/${c.id}`}>
                    <Button size="sm" variant="ghost" className="h-7"><ArrowSquareOut size={14} /></Button>
                  </Link>
                </li>
              ))}
            </ul>
          </Section>
        )}

        <Section title="Compromisos" icon={ListChecks} count={context.commitments.length}>
          {context.commitments.length === 0 ? <Empty /> : (
            <ul className="divide-y divide-slate-100">
              {context.commitments.map((cm) => (
                <li key={cm.id} className="py-2.5 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{cm.name}</div>
                    <div className="text-xs text-slate-500">{cm.tipo} · {formatDate(cm.fecha_compromiso) || "sin fecha"}</div>
                  </div>
                  <Semaforo value={cm.semaforo} />
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title="Proyectos" icon={Briefcase} count={context.projects.length}>
          {context.projects.length === 0 ? <Empty /> : (
            <ul className="divide-y divide-slate-100">
              {context.projects.map((p) => (
                <li key={p.id} className="py-2.5 flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{p.name}</div>
                    <div className="text-xs text-slate-500">{p.eje_estrategico || "—"}</div>
                  </div>
                  <EstadoBadge value={p.estado} />
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title="Reuniones" icon={Calendar} count={context.meetings.length}>
          {context.meetings.length === 0 ? <Empty /> : (
            <ul className="divide-y divide-slate-100">
              {context.meetings.map((m) => (
                <li key={m.id} className="py-2.5 flex items-center justify-between">
                  <div className="min-w-0">
                    <Link to={`/reuniones/${m.id}`} className="text-sm font-medium hover:underline">{m.asunto}</Link>
                    <div className="text-xs text-slate-500">{m.fecha} {m.lugar ? `· ${m.lugar}` : ""}</div>
                  </div>
                  <Link to={`/reuniones/${m.id}`}>
                    <Button size="sm" variant="ghost" className="h-7"><ArrowSquareOut size={14} /></Button>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-3">
      <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500">{label}</div>
      <div className="mt-1 font-display text-xl font-bold text-mono">{value}</div>
    </div>
  );
}

function Section({ title, icon: Icon, count, children }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-wider text-slate-700 font-medium flex items-center gap-1.5">
          <Icon size={14} /> {title}
        </div>
        <span className="text-xs text-slate-500">{count}</span>
      </div>
      <div className="rounded-md border border-slate-200 bg-white px-4">
        {children}
      </div>
    </div>
  );
}

function Empty({ text = "Sin registros." }) {
  return <div className="py-4 text-sm text-slate-500">{text}</div>;
}

function detailRouteForEntity(entity_type) {
  switch (entity_type) {
    case "comunal": return "autoridades-comunales";
    case "gobierno": return "autoridades-gobierno";
    case "parlamentario": return "parlamentarios";
    case "gore": return "autoridades-gore";
    case "organizacion": return "organizaciones";
    default: return "autoridades-comunales";
  }
}
